// Sales tab — deep sales analytics.
import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { fmtInt, fmtMoney, KpiCard } from "../components/KpiCard";
import { Card } from "./OverviewTab";
import { getSales } from "@/lib/analytics";
import { ShoppingBag, DollarSign, Target, Boxes } from "lucide-react";

const COLOR = { primary: "#E91E63", emerald: "#22C55E", amber: "#F59E0B", rose: "#F43F5E", purple: "#A855F7", cyan: "#06B6D4" };
const tip = { contentStyle: { background: "rgba(15, 15, 23, 0.95)", border: "1px solid rgba(91,91,214,0.4)", borderRadius: 8, fontSize: 12 } };
const DAYS_RO = ["Lun", "Mar", "Mie", "Joi", "Vin", "Sâm", "Dum"];

export default function SalesTab({ range }) {
  const [data, setData] = useState(null);
  useEffect(() => { getSales(range).then(setData); }, [JSON.stringify(range)]); // eslint-disable-line
  if (!data) return <div className="h-96 rounded-xl bg-card/40 border border-border animate-pulse" />;
  const pay = [
    { name: "COD", value: data.payment.cod.revenue, orders: data.payment.cod.orders, color: COLOR.amber },
    { name: "Card", value: data.payment.card.revenue, orders: data.payment.card.orders, color: COLOR.primary },
  ];
  const totalRev = data.totals.revenue || 1;
  // Heatmap normalization
  const flat = data.heatmap.flat();
  const maxCell = Math.max(1, ...flat);

  return (
    <div className="space-y-6" data-testid="analytics-sales">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Vânzări totale" value={data.totals.revenue} format="money" icon={DollarSign} color={COLOR.emerald} />
        <KpiCard label="Comenzi" value={data.totals.orders} icon={ShoppingBag} color={COLOR.primary} />
        <KpiCard label="AOV" value={data.totals.aov} format="money" icon={Target} color={COLOR.amber} />
        <KpiCard label="Produse vândute" value={data.totals.units} icon={Boxes} color={COLOR.emerald} />
      </div>

      {/* Trend + payment mix */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl bg-card/70 border border-border p-4">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-3">Vânzări vs comenzi pe zi</div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.series}>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="date" stroke="#71717a" fontSize={10} tickFormatter={(d) => d.slice(5)} />
              <YAxis yAxisId="l" stroke="#71717a" fontSize={10} />
              <YAxis yAxisId="r" orientation="right" stroke="#71717a" fontSize={10} />
              <Tooltip {...tip} formatter={(v, k) => (k === "revenue" ? `${fmtMoney(v)} RON` : v)} />
              <Line yAxisId="l" type="monotone" dataKey="revenue" stroke={COLOR.emerald} strokeWidth={2} dot={false} />
              <Line yAxisId="r" type="monotone" dataKey="orders" stroke={COLOR.primary} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <Card title="Plată">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={pay} dataKey="value" innerRadius={50} outerRadius={80} paddingAngle={3}>
                {pay.map((p, i) => <Cell key={i} fill={p.color} />)}
              </Pie>
              <Tooltip {...tip} formatter={(v) => fmtMoney(v) + " RON"} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1 mt-2">
            {pay.map((p) => (
              <div key={p.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2"><i className="h-2.5 w-2.5 rounded-full" style={{ background: p.color }} /> {p.name}</span>
                <span className="tabular-nums">{fmtInt(p.orders)} · {((p.value / totalRev) * 100).toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Heatmap by hour × weekday */}
      <Card title="Heatmap — ore × zile (comenzi)">
        <div className="overflow-x-auto">
          <table className="text-[10px] tabular-nums">
            <thead>
              <tr>
                <th className="px-1.5 py-1 text-muted-foreground">Ora</th>
                {DAYS_RO.map((d) => <th key={d} className="px-1.5 py-1 text-muted-foreground">{d}</th>)}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 24 }, (_, h) => (
                <tr key={h}>
                  <td className="px-1.5 py-0.5 text-muted-foreground">{String(h).padStart(2, "0")}:00</td>
                  {Array.from({ length: 7 }, (_, d) => {
                    const v = data.heatmap[h][d];
                    const t = v / maxCell;
                    const bg = `rgba(91,91,214,${0.08 + t * 0.85})`;
                    return (
                      <td key={d} className="px-1.5 py-0.5">
                        <div className="h-5 w-9 rounded text-center text-[9px] flex items-center justify-center font-mono" style={{ background: bg, color: t > 0.5 ? "#fff" : "#aaa" }}>
                          {v || ""}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Counties + couriers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Top județe după vânzări">
          <ResponsiveContainer width="100%" height={Math.max(200, Math.min(400, data.counties.length * 22))}>
            <BarChart data={data.counties.slice(0, 15)} layout="vertical" margin={{ left: 80 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" />
              <XAxis type="number" stroke="#71717a" fontSize={10} />
              <YAxis dataKey="county" type="category" stroke="#71717a" fontSize={10} width={75} />
              <Tooltip {...tip} formatter={(v) => fmtMoney(v) + " RON"} />
              <Bar dataKey="revenue" fill={COLOR.primary} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Performanță curieri">
          {data.couriers.length === 0 ? (
            <div className="text-center text-xs text-muted-foreground py-8">Niciun curier înregistrat</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase text-muted-foreground">
                <tr><th className="text-left py-2">Curier</th><th className="text-right">Comenzi</th><th className="text-right">Livrate</th><th className="text-right">Rată</th></tr>
              </thead>
              <tbody>
                {data.couriers.map((c) => (
                  <tr key={c.courier} className="border-t border-border">
                    <td className="py-2">{c.courier}</td>
                    <td className="text-right tabular-nums">{fmtInt(c.orders)}</td>
                    <td className="text-right tabular-nums">{fmtInt(c.delivered)}</td>
                    <td className="text-right tabular-nums font-semibold">{c.rate.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}
